import { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, ShieldAlert, Loader2, ChevronDown, Info } from "lucide-react";
import api from "@/lib/api";

/**
 * "Verify this decision" — recompute a stored score from its frozen evidence and
 * frozen weights, and show whether it still produces the same number.
 *
 * Light executive theme: this lives on the lender report, not the dark product
 * surface.
 *
 * A mismatch is rendered as prominently as a match. A system that reports its
 * own drift is more trustworthy than one that cannot, so amber here is a
 * feature rather than an error state to be buried.
 */

const SIGNAL_LABELS = {
    academic: "Academic",
    fee_payment: "Fee Payment",
    developer_professional: "Developer / Professional",
    social_creator: "Social / Creator",
    consumption: "Consumption",
    coverage_consistency: "Coverage & Consistency",
};

const label = (key) => SIGNAL_LABELS[key] || key;

function Row({ name, stored, recomputed, weight, drifted }) {
    return (
        <tr className={drifted ? "bg-amber-50" : undefined}>
            <td className="py-1.5 pr-2 text-slate-600">{label(name)}</td>
            <td className="py-1.5 px-1.5 text-right font-mono text-slate-700 tabular-nums">{stored ?? "—"}</td>
            <td className="py-1.5 px-1.5 text-right font-mono text-slate-700 tabular-nums">{recomputed ?? "—"}</td>
            <td className="hidden py-1.5 pl-2 text-right font-mono text-slate-500 tabular-nums lg:table-cell">
                {weight == null ? "—" : `${Math.round(weight * 100)}%`}
            </td>
        </tr>
    );
}

export default function DecisionVerification({ scoreId, isDemo = false }) {
    const [state, setState] = useState("idle"); // idle | loading | done | error
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const inFlight = useRef(false);

    const run = useCallback(async () => {
        // Double-click guard: one request, no duplicate work.
        if (inFlight.current) return;
        inFlight.current = true;
        setState("loading");
        setError(null);
        try {
            const { data } = await api.post(`/api/score/${scoreId}/verify`, null, { timeout: 20000 });
            setResult(data);
            setState("done");
            setOpen(true);
        } catch (err) {
            const status = err?.response?.status;
            setError(
                status === 403 || status === 404
                    ? "This record is not available to verify under your account."
                    : status === 400
                        ? "That score id is not a recorded decision."
                        : "Could not reach the verification service. Try again."
            );
            setState("error");
        } finally {
            inFlight.current = false;
        }
    }, [scoreId]);

    // Demo profiles are fixtures, not recorded decisions: there is no stored
    // snapshot to recompute from. Saying so is better than offering a button
    // that would explain itself only after failing.
    if (isDemo) {
        return (
            <div
                className="mt-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500"
                data-testid="verify-demo-note"
            >
                <Info size={13} className="mt-0.5 shrink-0" />
                <span>
                    Verification applies to recorded decisions. Generate a score to recompute it
                    from its own frozen evidence and weights.
                </span>
            </div>
        );
    }

    const matches = result?.matches;
    const unverifiable = result && result.verifiable === false;

    return (
        <div className="mt-3" data-testid="verify-decision">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={run}
                    disabled={state === "loading"}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60 min-h-[36px]"
                    data-testid="verify-decision-btn"
                >
                    {state === "loading" ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                    {state === "loading" ? "Recomputing…" : state === "done" ? "Verify again" : "Verify this decision"}
                </button>

                {state === "done" && !unverifiable && (
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            matches
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                        aria-expanded={open}
                        data-testid="verify-decision-badge"
                    >
                        {matches ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
                        {matches
                            ? `Reproduced ${result.recomputed?.score} · ${result.scorecard_version}`
                            : `Drift detected · ${result.deltas?.length || 0} signal${result.deltas?.length === 1 ? "" : "s"}`}
                        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                )}
            </div>

            {state === "error" && (
                <p className="mt-2 text-xs text-amber-700" role="alert" data-testid="verify-decision-error">
                    {error}
                </p>
            )}

            {state === "done" && unverifiable && (
                <p className="mt-2 text-xs text-slate-500" data-testid="verify-decision-unverifiable">
                    {result.reason}
                </p>
            )}

            <AnimatePresence initial={false}>
                {open && state === "done" && !unverifiable && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                        data-testid="verify-decision-detail"
                    >
                        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Recomputed from {result.evidence_count} frozen evidence item
                                {result.evidence_count === 1 ? "" : "s"} using scorecard{" "}
                                <span className="font-mono text-slate-700">{result.scorecard_version}</span>, the
                                version recorded with this decision — not the current default.
                            </p>

                            <div className="mt-3 overflow-x-auto">
                                <table className="w-full min-w-[260px] text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-200 text-slate-500">
                                            <th className="py-1.5 pr-3 text-left font-medium">Signal</th>
                                            <th className="py-1.5 px-2 text-right font-medium">Stored</th>
                                            <th className="py-1.5 px-2 text-right font-medium">Recomputed</th>
                                            <th className="hidden py-1.5 pl-2 text-right font-medium lg:table-cell">Weight</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {Object.entries(result.stored?.signal_scores || {}).map(([name, entry]) => {
                                            const re = result.recomputed?.signal_scores?.[name];
                                            const drifted = (result.deltas || []).some((d) => d.signal === name);
                                            return (
                                                <Row
                                                    key={name}
                                                    name={name}
                                                    stored={entry?.score}
                                                    recomputed={re?.score}
                                                    weight={entry?.weight}
                                                    drifted={drifted}
                                                />
                                            );
                                        })}
                                        <tr className="border-t-2 border-slate-200 font-semibold">
                                            <td className="py-2 pr-3 text-slate-700">Total</td>
                                            <td className="py-2 px-2 text-right font-mono text-slate-900 tabular-nums">
                                                {result.stored?.score}
                                            </td>
                                            <td className={`py-2 px-2 text-right font-mono tabular-nums ${matches ? "text-emerald-700" : "text-amber-700"}`}>
                                                {result.recomputed?.score}
                                            </td>
                                            <td className="hidden py-2 pl-2 text-right text-slate-400 lg:table-cell">—</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {!matches && (result.deltas || []).length > 0 && (
                                <ul className="mt-3 space-y-1 text-xs text-amber-700" data-testid="verify-decision-deltas">
                                    {result.deltas.map((d) => (
                                        <li key={d.signal}>
                                            {label(d.signal)}: stored {d.stored ?? "—"}, recomputed {d.recomputed ?? "—"}
                                            {d.delta != null && ` (${d.delta > 0 ? "+" : ""}${d.delta})`}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
